<template>
  <div class="city-channel-comparison">
    <div class="module-header">
      <h3 class="module-title">各{{currentRegion.level == 'city' ? '区县' : '市州'}}渠道对比</h3>
      <div class="time-selector">
        <button
          v-for="option in timeOptions"
          :key="option.value"
          :class="['time-btn', { active: cityChannelComparison.period === option.value }]"
          @click="changePeriod(option.value)"
        >
          {{ option.label }}
        </button>
      </div>
    </div>
    <div class="comparison-content">
      <div ref="chartRef" class="chart"></div>
    </div>
  </div>
  </template>

<script>
import { mapState, mapActions } from 'vuex'
import * as echarts from 'echarts'

export default {
  name: 'CityChannelComparison',
  data() {
    return {
      chart: null,
      // 滚动窗口相关
      scrollTimer: null,
      displayStart: 0,
      displayCount: 9,
      isPaused: false,
      timeOptions: [
        { label: '今日', value: 'today' },
        { label: '本周', value: 'week' },
        { label: '本月', value: 'month' }
      ],
      // 数据变化检测
      lastDataSnapshot: null,
      isDataRefreshing: false // 标记是否正在进行数据刷新
    }
  },
  computed: {
    ...mapState('city', ['cityChannelComparison']),
    ...mapState('region', ['currentRegion']),
    // 数据预处理：取前10城市，按线上/线下聚合，单位转换为万元
    preparedData() {
      const cities = this.cityChannelComparison?.cities || []
      if (!Array.isArray(cities) || cities.length === 0) {
        return { cityNames: [], online: [], offline: [] }
      }
      const sorted = [...cities]
        .sort((a, b) => (b.totalAmount || 0) - (a.totalAmount || 0))
        .slice(0, 14)
      const cityNames = []
      const online = []
      const offline = []
      sorted.forEach((city) => {
        cityNames.push(city.cityName)
        const channels = Array.isArray(city.channels) ? city.channels : []
        const onlineAmount = channels
          .filter(c => c.channelName === '线上')
          .reduce((sum, c) => sum + (Number(c.salesAmount) || 0), 0)
        const offlineAmount = channels
          .filter(c => c.channelName !== '线上')
          .reduce((sum, c) => sum + (Number(c.salesAmount) || 0), 0)
        online.push(Number((onlineAmount / 10000).toFixed(2)))
        offline.push(Number((offlineAmount / 10000).toFixed(2)))
      })
      return { cityNames, online, offline }
    },
    // 计算全局固定的x轴最大值，基于所有数据
    globalAxisMax() {
      const { online, offline } = this.preparedData
      return this.computeAxisMax([...online, ...offline])
    }
  },
  mounted() {
    this.initChart()
    this.fetchCityChannelComparison('today').then(() => {
      this.$nextTick(() => {
        // 初始化数据快照
        this.updateDataSnapshot(this.cityChannelComparison?.cities || [])
        this.updateChart()
        this.setupAutoScroll()
      })
    }).catch(error => {
      console.error('获取市州渠道对比数据失败:', error)
    })
  },
  beforeDestroy() {
    if (this.chart) this.chart.dispose()
    this.clearAutoScroll()
  },
  watch: {
    'cityChannelComparison.cities': {
      handler(newCities) {
        // 检查数据是否真正发生变化
        if (this.hasDataChanged(newCities)) {
          this.updateDataSnapshot(newCities)
          
          // 如果不是静默刷新（用户主动切换时间段），则重置滚动位置
          if (!this.isDataRefreshing) {
            this.displayStart = 0
          }
          
          this.$nextTick(() => {
            this.updateChart()
            // 只有在数据真正变化时才重新设置滚动
            if (!this.isDataRefreshing) {
              this.setupAutoScroll()
            }
          })
        }
        
        // 重置刷新标记
        this.isDataRefreshing = false
      },
      deep: true
    }
  },
  methods: {
    ...mapActions('city', ['fetchCityChannelComparison']),
    
    // 检查数据是否发生实质性变化
    hasDataChanged(newCities) {
      if (!this.lastDataSnapshot) return true
      
      const newData = Array.isArray(newCities) ? newCities : []
      if (newData.length !== this.lastDataSnapshot.length) return true
      
      return newData.some((city, idx) => {
        const oldCity = this.lastDataSnapshot[idx]
        if (!oldCity || city.cityName !== oldCity.cityName) return true
        
        // 检查渠道数据是否变化
        const newChannels = Array.isArray(city.channels) ? city.channels : []
        const oldChannels = Array.isArray(oldCity.channels) ? oldCity.channels : []
        
        if (newChannels.length !== oldChannels.length) return true
        
        return newChannels.some((channel, channelIdx) => {
          const oldChannel = oldChannels[channelIdx]
          return !oldChannel ||
                 channel.channelName !== oldChannel.channelName ||
                 Math.abs((channel.salesAmount || 0) - (oldChannel.salesAmount || 0)) > 0.01
        })
      })
    },
    
    // 更新数据快照
    updateDataSnapshot(cities) {
      this.lastDataSnapshot = (Array.isArray(cities) ? cities : []).map(city => ({
        cityName: city.cityName,
        totalAmount: city.totalAmount || 0,
        channels: (Array.isArray(city.channels) ? city.channels : []).map(channel => ({
          channelName: channel.channelName,
          salesAmount: channel.salesAmount || 0
        }))
      }))
    },
    
    // 静默刷新数据（保持滚动状态）
    async silentRefresh() {
      if (!this.cityChannelComparison?.period) return
      
      this.isDataRefreshing = true
      try {
        await this.fetchCityChannelComparison()
      } catch (error) {
        console.error('CityChannelComparison: 静默刷新失败', error)
        this.isDataRefreshing = false
      }
    },
    // 计算“好看”的坐标轴最大值（1-2-5 整数刻度）
    computeAxisMax(values) {
      const arr = Array.isArray(values) ? values : []
      const vmax = arr.reduce((m, v) => Math.max(m, Number(v) || 0), 0)
      if (!isFinite(vmax) || vmax <= 0) return 5
      const magnitude = Math.pow(10, Math.floor(Math.log10(vmax)))
      const norm = vmax / magnitude
      let scaled
      if (norm <= 1) scaled = 0.5
      else if (norm <= 2) scaled = 1
      else if (norm <= 4) scaled = 1.5
      else scaled = 2
      const nice = scaled * magnitude
      // 保证为整数显示，且不小于实际最大值
      return Math.max(Math.ceil(nice), Math.ceil(vmax))
    },
    initChart() {
      this.$nextTick(() => {
        if (this.$refs.chartRef) {
          this.chart = echarts.init(this.$refs.chartRef)
          const resizeHandler = () => this.chart && this.chart.resize()
          window.addEventListener('resize', resizeHandler)
          this.$once('hook:beforeDestroy', () => {
            window.removeEventListener('resize', resizeHandler)
          })

          // 悬停暂停滚动
          const container = this.$refs.chartRef
          if (container) {
            container.addEventListener('mouseenter', () => { this.isPaused = true })
            container.addEventListener('mouseleave', () => { this.isPaused = false })
          }
        }
      })
    },
    // 根据 displayStart 与 displayCount 生成循环窗口数据
    getWindowedData() {
      const { cityNames, online, offline } = this.preparedData
      const total = cityNames.length
      if (total <= this.displayCount) {
        return { cityNames, online, offline }
      }
      const names = []
      const onArr = []
      const offArr = []
      for (let i = 0; i < this.displayCount; i++) {
        const idx = (this.displayStart + i) % total
        names.push(cityNames[idx])
        onArr.push(online[idx])
        offArr.push(offline[idx])
      }
      return { cityNames: names, online: onArr, offline: offArr }
    },
    setupAutoScroll() {
      this.clearAutoScroll()
      const total = this.preparedData.cityNames.length
      if (total <= this.displayCount) return
      
      // 每 2.5s 往下滚动一条，循环
      this.scrollTimer = setInterval(() => {
        if (this.isPaused) return
        this.displayStart = (this.displayStart + 1) % total
        this.updateChart()
      }, 2500)
    },
    clearAutoScroll() {
      if (this.scrollTimer) {
        clearInterval(this.scrollTimer)
        this.scrollTimer = null
      }
    },
    updateChart() {
      if (!this.chart) return
      const { cityNames, online, offline } = this.getWindowedData()
      // console.log('%c City window', 'color:#33a5ff', this.displayStart, cityNames)
      if (cityNames.length === 0) return
      
      // 使用全局固定的 x 轴最大值，确保滚动时x轴保持稳定
      const axisMax = this.globalAxisMax

      const option = {
        grid: {
          left: 60,
          right: 20,
          top: 40,
          bottom: 20,
          containLabel: false
        },
        // 优化动画配置，减少重绘开销
        animation: true,
        animationDuration: 300, // 减少动画时长
        animationDurationUpdate: 400, // 减少更新动画时长
        animationEasing: 'cubicOut',
        animationEasingUpdate: 'cubicOut',
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params) => {
            const name = params[0]?.name || ''
            const ov = params.find(p => p.seriesName === '线上征订')?.value ?? 0
            const fv = params.find(p => p.seriesName === '线下零售')?.value ?? 0
            return `${name}<br/>线上征订：${ov} 万元<br/>线下零售：${fv} 万元`
          },
          backgroundColor: 'rgba(0,0,0,0.8)',
          borderColor: 'rgba(0,0,0,0.8)',
          textStyle: { color: '#fff', fontSize: 12 }
        },
        legend: {
          top: 10,
          right: 0,
          itemWidth: 9,
          itemHeight: 9,
          textStyle: { color: '#FFFFFF', fontSize: 12 },
          data: [
            { name: '线上征订', icon: 'circle' },
            { name: '线下零售', icon: 'circle' }
          ]
        },
        xAxis: {
          type: 'value',
          max: axisMax,
          min: 0, // 确保x轴从0开始，提供更好的视觉一致性
          nameTextStyle: { color: '#C0D8F0', fontSize: 12 },
          axisLine: { lineStyle: { color: 'rgba(192,216,240,0.4)' } },
          axisLabel: { 
            color: '#C0D8F0',
            formatter: '{value}' // 确保数值格式一致
          },
          splitLine: { show: true, lineStyle: { type: 'dashed', color: 'rgba(192,216,240,0.2)' } }
        },
        yAxis: {
          name: '单位：万元',
          nameLocation: "start",
          nameTextStyle: {
            color: "#ffffff",
            align: "right",
            fontSize: 12,
            width: 30,
            // padding: [0, 0, 0, 50]
          },
          type: 'category',
          inverse: true,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: '#C0D8F0',
            width: 50,
            overflow: 'truncate',
            margin: 14
          },
          data: cityNames
        },
        series: [
          {
            name: '线上征订',
            type: 'bar',
            barGap: 0,
            barCategoryGap: 14,
            data: online,
            barWidth: 10,
            itemStyle: {
              color: '#2391FF',
              borderRadius: [0, 2, 2, 0]
            },
            // 优化动画
            animationDelay: (idx) => idx * 50
          },
          {
            name: '线下零售',
            type: 'bar',
            barGap: 0,
            barCategoryGap: 14,
            data: offline,
            barWidth: 10,
            itemStyle: {
              color: '#5AD8A6',
              borderRadius: [0, 2, 2, 0]
            },
            // 优化动画
            animationDelay: (idx) => idx * 50 + 100
          }
        ]
      }

      try {
        // 使用 notMerge: false 来优化性能，只更新变化的部分
        this.chart.setOption(option, false)
      } catch (error) {
        console.error('CityChannelComparison: 更新图表失败', error)
      }
    },
    async changePeriod(period) {
      if (period !== this.cityChannelComparison.period) {
        try {
          await this.fetchCityChannelComparison(period)
          this.displayStart = 0
          this.$nextTick(() => {
            this.updateChart()
            this.setupAutoScroll()
          })
        } catch (error) {
          console.error('CityChannelComparison: 更改周期失败', error)
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.city-channel-comparison {
  height: 100%;
  display: flex;
  flex-direction: column;

  .comparison-content {
    flex: 1;
    min-height: 340px;
    display: flex;
    flex-direction: column;
    position: relative;

    .chart {
      width: 100%;
      height: 360px;
    }
  }
}
</style>